import { Menu, Portal } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { FC, useState, useRef, useEffect } from 'react'
import { AppStore, BottomRightCorner } from 'stylewhere/shared'

interface Props {
  onEmulationClick: () => void
  enabled: boolean
}

export const EmulationButton: FC<Props> = ({ onEmulationClick, enabled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMenuOpen) {
        const target = e.target as Node
        if (
          buttonRef.current &&
          !buttonRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target) &&
          !(e.target as Element)?.closest('[data-part="content"]')
        ) {
          setIsMenuOpen(false)
        }
      }
    }

    if (isMenuOpen) {
      // Usa un piccolo delay per evitare che il click destro chiuda immediatamente
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 100)
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [isMenuOpen])

  const visible = enabled && AppStore.getEmulation() && !isHidden

  useEffect(() => (visible && buttonRef.current ? BottomRightCorner.track(buttonRef.current) : undefined), [visible])

  if (!visible) return null

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isMenuOpen) {
      onEmulationClick()
    }
  }

  const openModal = () => {
    onEmulationClick()
    setIsMenuOpen(false)
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Posiziona il trigger nascosto nella posizione del click (coordinate del mouse)
    if (triggerRef.current) {
      triggerRef.current.style.left = `${e.clientX}px`
      triggerRef.current.style.top = `${e.clientY}px`
      triggerRef.current.style.width = '1px'
      triggerRef.current.style.height = '1px'
    }
    setIsMenuOpen(true)
  }

  const handleMenuOpenChange = (e: { open: boolean }) => {
    setIsMenuOpen(e.open)
  }

  return (
    <>
      <StyledButton ref={buttonRef} onClick={handleClick} onContextMenu={handleContextMenu}>
        Emulate
      </StyledButton>
      <Menu.Root open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
        <Menu.Trigger asChild>
          <HiddenDiv ref={triggerRef} />
        </Menu.Trigger>
        <Portal>
          <StyledPositioner>
            <Menu.Content>
              <Menu.Item value="open" onClick={openModal}>
                Open Emulation Modal
              </Menu.Item>
              <Menu.Item
                value="hide"
                onClick={() => {
                  setIsHidden(true)
                  setIsMenuOpen(false)
                }}
              >
                Hide Emulation Button
              </Menu.Item>
            </Menu.Content>
          </StyledPositioner>
        </Portal>
      </Menu.Root>
    </>
  )
}

const StyledButton = styled.button`
  position: fixed;
  right: 10px;
  bottom: 10px;
  background: #8b5cf6;
  color: #ffffff;
  border-radius: 20px;
  padding: 8px 16px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 2px 10px rgba(139, 92, 246, 0.3);
  z-index: 1000;
  display: flex;
  align-items: center;
  user-select: none;

  &:hover {
    background: #7c3aed;
  }
`

const HiddenDiv = styled.div`
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 0;
  height: 0;
  pointer-events: none;
`

const StyledPositioner = styled(Menu.Positioner)`
  z-index: 1001;
`
